import React, { useState, useEffect, useMemo, useCallback, Fragment } from "react";
import api from "../api/axios";
import {
  CircularProgress,
  Grid,
  TextField,
  Autocomplete,
  Fade,
  Box,
  Typography,
  Button,
  Collapse,
} from "@mui/material/";

import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Book,
  Search,
  CleaningServices,
} from "@mui/icons-material";
import Table from "../Components/Common/Table/Table";
import { CommonSubHeader } from "../Components/helpers/Helpers";
import TableActions from "../Components/Common/Table/TableActions";
import LoadingPage from "../Components/Common/Loading/LoadingPage";

// table
import { motion } from "framer-motion";

import { CustomButtonPlusIcon } from "../Components/Common/Design/CustomButtonPlusIcon";

function Biblioteca() {
  const DEFAULT_FILTERS = {
    title: "",
    tag: "",
    description: "",
    category: null,
    objPerPage: 10,
  };

  const [pagePressed, setPagePressed] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [library, setLibrary] = useState([]);
  const [totalPagesNumber, setTotalPagesNumber] = useState(0);
  const [flag, setFlag] = useState(false);
  const [changeDone, setChangeDone] = useState(false);

  // Filtros de búsqueda
  const [showFilters, setShowFilters] = useState(true);

  // draftFilters: lo que el usuario escribe
  // appliedFilters: lo que realmente se envía al backend al pulsar Buscar
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);

  const [openOptionsCategories, setOpenOptionsCategories] = useState(false);
  const [optionsCategories, setOptionsCategories] = useState([]);
  const loadingOptionsCategories =
    openOptionsCategories && optionsCategories.length === 0;

  const smoothLayoutTransition = {
    layout: {
      type: "spring",
      stiffness: 185,
      damping: 30,
      mass: 0.85,
    },
  };

  const handleFilterStates = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setPagePressed(1);
  };

  const handleSearch = () => {
    setPagePressed(1);
    // Se aplica una copia para garantizar refresh aun cuando los valores sean iguales
    setAppliedFilters((_) => ({ ...draftFilters }));
  };

  // Obtener los datos de la biblioteca
  const getDataLibrary = useCallback(async () => {
    setFlag(true);
    try {
      const { title, tag, description, category, objPerPage } = appliedFilters;

      const parametros = {
        page: pagePressed,
        objPerPage,
        title,
        category,
        tags: tag,
        description,
      };

      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/library`,
        parametros,
      );

      const { library: responseApi, categories } = data;
      setLibrary(responseApi.data);
      setPageCount(responseApi.last_page);
      setTotalPagesNumber(responseApi.total);
      setOptionsCategories([{ category: "Seleccionar" }, ...categories]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setFlag(false);
    }
  }, [appliedFilters, pagePressed]);

  // Función para renderizar celdas con un valor por defecto
  const renderCellWithDefault = (value, defaultValue = "Sin asignar") => (
    <div
      className="spanClassForTables centerContent"
      style={{ padding: "5px" }}
    >
      {value || defaultValue}
    </div>
  );

  // Columnas de la tabla
  const columns = useMemo(
    () => [
      {
        Header: "Biblioteca de Archivos",
        columns: [
          {
            Header: "Titulo",
            accessor: "title",
            Cell: ({ value }) => renderCellWithDefault(value),
          },
          {
            Header: "Categoría",
            accessor: "category",
            width: 80,
            Cell: ({ value }) => renderCellWithDefault(value),
          },
          {
            Header: "Etiquetas",
            accessor: "tags",
            width: 300,
            Cell: ({ value }) => renderCellWithDefault(value),
          },
          {
            Header: "Descripción",
            accessor: "description",
            width: 300,
            Cell: ({ value }) =>
              renderCellWithDefault(
                value ? value.replace(/<br\s*\/?>/gi, "\n") : value,
              ),
          },
          {
            Header: "Acciones",
            accessor: "id",
            width: 50,
            Cell: ({ value }) => (
              <TableActions
                rute={"biblioteca"}
                value={value}
                showView
                showEdit
                showDelete
                apiRute={"library"}
                module="library"
                setChangeDone={setChangeDone}
                changeDone={changeDone}
              />
            ),
          },
        ],
      },
    ],
    [changeDone],
  );

  // Manejadores de cambio en filtros
  const handleFilterChange = (field) => (event) => {
    setDraftFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleCategoryChange = (_, value) => {
    const normalizedCategory =
      value && value.category !== "Seleccionar" ? value.category : null;
    setDraftFilters((prev) => ({ ...prev, category: normalizedCategory }));
  };

  const handlePageClick = ({ selected }) => setPagePressed(selected + 1);

  // Efectos
  useEffect(() => {
    getDataLibrary();
  }, [getDataLibrary, changeDone]);

  useEffect(() => {
    if (!loadingOptionsCategories) return;
    setTimeout(() => setOptionsCategories((prev) => [...prev]), 200);
  }, [loadingOptionsCategories]);

  useEffect(() => {
    if (pagePressed > pageCount) {
      setPagePressed(1);
    }
  }, [pageCount, pagePressed]);

  // Estructura de campos para mapeo
  const fields = useMemo(
    () => [
      {
        type: "text",
        label: "Titulo",
        value: draftFilters.title,
        onChange: handleFilterChange("title"),
      },
      {
        type: "autocomplete",
        label: "Categorías",
        value: optionsCategories.find(
          (option) => option.category === draftFilters.category,
        ) || null,
        open: openOptionsCategories,
        onOpen: () => setOpenOptionsCategories(true),
        onClose: () => setOpenOptionsCategories(false),
        onChange: handleCategoryChange,
        options: optionsCategories,
        loading: loadingOptionsCategories,
      },
      {
        type: "text",
        label: "Etiqueta",
        value: draftFilters.tag,
        onChange: handleFilterChange("tag"),
      },
      {
        type: "text",
        label: "Descripción",
        value: draftFilters.description,
        onChange: handleFilterChange("description"),
      },
      {
        type: "select",
        label: "Nº Registros",
        value: draftFilters.objPerPage,
        onChange: (e) =>
          setDraftFilters((prev) => ({
            ...prev,
            objPerPage: Number(e.target.value),
          })),
        options: [10, 20, 30, 100, 200],
      },
    ],
    [
      draftFilters,
      openOptionsCategories,
      optionsCategories,
      loadingOptionsCategories,
    ],
  );

  return (
    <div className="fatherContainer">
      <CommonSubHeader title="Biblioteca" />

      <div className="mainContainer">
        <div className="subMainContainer">
          <Box
            component={motion.div}
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            // transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
            transition={smoothLayoutTransition}
            sx={{
              position: "relative",
              background:
                "linear-gradient(170deg, rgba(255,255,255,0.98) 0%, rgba(246,249,252,0.95) 100%)",
              border: "1px solid rgba(28, 48, 74, 0.12)",
              marginBottom: "8px",
              mx: "8px",
              borderRadius: "12px",
              // boxShadow:
              //   "0 2px 8px rgba(13, 24, 38, 0.05), 0 10px 22px rgba(13, 24, 38, 0.07)",
              backdropFilter: "blur(2px)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                px: { xs: 1.2, md: 1.6 },
                py: 0.85,
                background:
                  "linear-gradient(92deg, rgba(75, 105, 147, 0.12) 0%, rgba(27, 54, 93, 0.03) 72%)",
                borderBottom: "1px solid rgba(28, 48, 74, 0.11)",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: "#17345a",
                  letterSpacing: "0.03em",
                  fontSize: "",
                }}
              >
                Filtros de búsqueda
              </Typography>

              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowFilters(!showFilters)}
                endIcon={
                  showFilters ? <KeyboardArrowUp /> : <KeyboardArrowDown />
                }
                sx={{
                  minWidth: { xs: "36px", sm: "156px" },
                  width: { xs: "36px", sm: "auto" },
                  height: "30px",
                  px: { xs: 0.25, sm: 1.2 },
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.78rem",
                  borderColor: "rgba(23, 52, 90, 0.26)",
                  color: "#17345a",
                  backgroundColor: "rgba(255,255,255,0.88)",
                  transition:
                    "all 220ms cubic-bezier(0.22,1,0.36,1), box-shadow 220ms cubic-bezier(0.22,1,0.36,1)",
                  "& .MuiButton-endIcon": {
                    marginLeft: { xs: 0, sm: 0.5 },
                    marginRight: 0,
                  },
                  "&:hover": {
                    borderColor: "rgba(23, 52, 90, 0.42)",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 4px 12px rgba(16, 34, 56, 0.16)",
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{ display: { xs: "none", sm: "inline" } }}
                >
                  {showFilters ? "Ocultar filtros" : "Mostrar filtros"}
                </Box>
              </Button>
            </Box>

            <Collapse
              in={showFilters}
              timeout={{ enter: 380, exit: 280 }}
              easing={{
                enter: "cubic-bezier(0.22,1,0.36,1)",
                exit: "cubic-bezier(0.4,0,0.2,1)",
              }}
              sx={{ transformOrigin: "top center" }}
            >
              <Grid
                container
                rowSpacing={1.1}
                columnSpacing={1}
                sx={{
                  px: { xs: 0.75, md: 1.15 },
                  py: 0.95,
                  backgroundColor: "transparent",
                }}
              >
                {(fields || []).map((field, index) => {
                  const commonInputSx = {
                    width: "100%",
                    "& .MuiInputLabel-root": {
                      fontSize: "0.72rem",
                      color: "#4b6079",
                      transform: "translate(10px, 8px) scale(1)",
                      transformOrigin: "top left",
                      lineHeight: 1,
                    },
                    "& .MuiInputLabel-shrink": {
                      transform: "translate(10px, -7px) scale(0.75)",
                      backgroundColor: "#ffffff",
                      padding: "0 4px",
                      borderRadius: "4px",
                    },
                    "& .MuiInputBase-root": {
                      height: "34px !important",
                      borderRadius: "8px",
                      backgroundColor: "#ffffff",
                      fontSize: "0.8rem",
                      transition:
                        "box-shadow 220ms ease, border-color 220ms ease, background-color 220ms ease, transform 220ms ease",
                    },
                    "& .MuiInputBase-input": {
                      padding: "0 9px",
                      color: "#1c2f47",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(28, 48, 74, 0.17)",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& legend": {
                        fontSize: "0.72rem",
                      },
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(23, 52, 90, 0.36)",
                    },
                    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(23, 52, 90, 0.66)",
                      borderWidth: "1px",
                    },
                    "& .Mui-focused": {
                      boxShadow: "0 0 0 2px rgba(23, 52, 90, 0.12)",
                    },
                  };

                  let component = null;

                  switch (field.type) {
                    case "text":
                      component = (
                        <TextField
                          label={field.label}
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={field.value}
                          onChange={field.onChange}
                          sx={commonInputSx}
                        />
                      );
                      break;

                    case "autocomplete":
                      component = (
                        <Autocomplete
                          size="small"
                          open={field.open}
                          onOpen={field.onOpen}
                          value={field?.value}
                          onClose={field.onClose}
                          onChange={field.onChange}
                          getOptionLabel={(option) => option.category}
                          options={field.options}
                          loading={field.loading}
                          sx={commonInputSx}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={field.label}
                              sx={commonInputSx}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {field.loading && (
                                      <CircularProgress
                                        color="inherit"
                                        size={20}
                                      />
                                    )}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      );
                      break;

                    case "select":
                      component = (
                        <TextField
                          select
                          label={field.label}
                          value={field.value}
                          onChange={field.onChange}
                          fullWidth
                          size="small"
                          sx={commonInputSx}
                          SelectProps={{ native: true }}
                        >
                          {field.options.map((optionValue) => (
                            <option key={optionValue} value={optionValue}>
                              {optionValue}
                            </option>
                          ))}
                        </TextField>
                      );
                      break;

                    default:
                      component = null;
                  }

                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      key={`${field.type}-${field.label}-${index}`}
                    >
                      {component}
                    </Grid>
                  );
                })}

                {/* BOTONES */}

                <Grid
                  item
                  xs={12}
                  md={12}
                  textAlign="center"
                  sx={{
                    display: "inline-flex",
                    flexWrap: "wrap",
                    gap: "6px",
                    justifyContent: "center",
                    mt: 0.55,
                    pt: 0.9,
                    borderTop: "1px solid rgba(28, 48, 74, 0.12)",
                  }}
                >
                  <CustomButtonPlusIcon
                    icon={Search}
                    text={"BUSCAR"}
                    handleFunction={handleSearch}
                    position={"start"}
                    variant={"outlined"}
                    color={"info"}
                  />
                  <CustomButtonPlusIcon
                    icon={CleaningServices}
                    text={"LIMPIAR FILTROS"}
                    handleFunction={handleFilterStates}
                    position={"start"}
                    variant={"outlined"}
                    color={"error"}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Box>

          {flag ? (
            <LoadingPage isLoading={true} heightProp={"600px"} />
          ) : (
            <Fade in={library?.length > 0} timeout={600}>
              <div>
                <Table
                  columns={columns}
                  data={library}
                  view={"libro"}
                  viewTitle={"biblioteca"}
                  totalPagesNumber={totalPagesNumber}
                  icon={Book}
                  // Pagination
                  onPageChange={handlePageClick}
                  pageCount={pageCount}
                  forcePage={pagePressed - 1}
                />
              </div>
            </Fade>
          )}
        </div>
      </div>
    </div>
  );
}

export default Biblioteca;
